import moment from "moment";
import React, { useEffect } from "react";
import { FlightFormState, getUpdateFlightInstruction, mapFlightToState } from "shared/utils/fieldsHelper/flightFields";

export const useFlightDetail = (
  flight: server.dto.Flight,
  passengers: server.dto.FlightPassenger[],
  onArtistChanged: (newArtist: server.dto.ArtistSmall) => void,
  onDateChanged: (newDate: server.dto.AppointmentDateTime) => void,
  onAirportChanged: () => void,
  onStatusChanged: () => void,
) => {
  const [isCreatingPassenger, setIsCreatingPassenger] = React.useState(false);
  const [state, setState] = React.useState<FlightFormState>(mapFlightToState(flight, passengers, serverReference));

  useEffect(() => {
    setState(mapFlightToState(flight, passengers, serverReference));
  }, [flight, passengers]);

  const onValueChanged = (key: string, value: any) => {
    if (key === "artist") {
      setState({ ...state, artist: value });
    } else if (key === "status") {
      setState({ ...state, phase: value });
    } else if (key === "travelAgency") {
      setState({ ...state, travelAgency: value });
    } else if (key === "notes") {
      setState({ ...state, notes: value });
    } else if (key === "airline") {
      setState({ ...state, airline: value });
    } else if (key === "flightDesignator") {
      setState({ ...state, flightDesignator: value });
    } else if (key === "departureLocal") {
      const diff = value.diff(state.departureLocal, "minutes");
      const newArrivalLocal = state.arrivalLocal.clone().add(diff, "minutes");
      setState({
        ...state,
        departureLocal: value,
        arrivalLocal: newArrivalLocal,
      });
    } else if (key === "departureAirport") {
      setState({ ...state, departureAirport: value });
    } else if (key === "departureTerminal") {
      setState({ ...state, departureTerminal: value });
    } else if (key === "arrivalLocal") {
      setState({ ...state, arrivalLocal: value });
    } else if (key === "arrivalAirport") {
      setState({ ...state, arrivalAirport: value });
    } else if (key === "arrivalTerminal") {
      setState({ ...state, arrivalTerminal: value });
    } else if (key === "access") {
      setState({ ...state, access: value });
    } else if (key === "confirmationNumber") {
      setState({ ...state, confirmationNumber: value });
    } else if (key === "capacity") {
      setState({ ...state, capacity: value });
    }
  }

  const refetchPassengers = async () => {
    const updatedPassengers = await window.CoreApi.Flight.GetSidePanelData(flight.flightId);

    if (updatedPassengers.data.hasErrors) {
      return;
    }

    setState((prev) => ({ ...prev, passengers: updatedPassengers.data.data.passengers }));
  }

  const onSuccessfulStatusUpdate = () => {
    onStatusChanged();
  }

  const onSuccessfulDateUpdate = async (key: string, value: moment.Moment) => {
    const departureLocal = key === "departureLocal" ? value : state.departureLocal;
    let arrivalLocal = key === "arrivalLocal" ? value : state.arrivalLocal;

    if (key === "departureLocal") {
      const diff = value.diff(state.departureLocal, "minutes");
      arrivalLocal = state.arrivalLocal.clone().add(diff, "minutes");
    }

    onDateChanged({
      startDisplay: departureLocal.toDate(),
      endDisplay: arrivalLocal.toDate(),
      mode: "hasTime",
      customFallbackTime: "",
    });
  }

  const onSuccessfulAirportUpdate = () => {
    onAirportChanged();
  }

  const onSuccessfulArtistUpdate = (_: string, value: server.dto.ArtistSmall) => {
    onArtistChanged(value);
  }

  const onSuccessfulUpdate = async (key: string, value: any) => {
    if (key === "status") {
      onSuccessfulStatusUpdate();
      return;
    }

    if (key === "arrivalLocal" || key === "departureLocal") {
      onSuccessfulDateUpdate(key, value);
      return;
    }

    if (key === "arrivalAirport" || key === "departureAirport") {
      onSuccessfulAirportUpdate();
      return;
    }

    if (key === "artist") {
      onSuccessfulArtistUpdate(key, value);
      return;
    }
  }

  return {
    state,
    onValueChanged,
    onSuccessfulUpdate,
    getUpdateFlightInstruction,
    refetchPassengers,
    isCreatingPassenger,
    setIsCreatingPassenger,
  }
}